import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';

//logos

import LogoShipperGuide from '@/public/images/icons/icon_shipperguide.svg';

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: `March 27, 2023`,
  title: `What is Full Truckload (FTL) vs. Less-Than-Truckload (LTL)?`,
  slug: `what-is-ftl-vs-ltl`,
  highlight: (
    <>
      <p>
        When it comes to shipping goods over the road in a truck, there are two
        main types of transport: Full Truckload (FTL) and Less-Than-Truckload
        (LTL).
      </p>
      <p>
        The difference between the two modes is important for companies to
        understand because each method of shipping has its advantages when it
        comes to cost and service. And depending on a specific load’s
        requirements, either can be the better choice. This is why manufacturers
        and retailers need to understand how full truckload and
        less-than-truckload work and the factors that make one a better option
        than the other in different situations.
      </p>
    </>
  ),
  copy: (
    <>
      <nav>
        <ul>
          <li>
            <a href="#what-is-full-truckload-shipping">
              What is Full Truckload shipping?
            </a>
          </li>
          <li>
            <a href="#what-is-less-than-truckload-shipping">
              What is Less-Than-Truckload (LTL) Shipping?
            </a>
          </li>
          <li>
            <a href="#how-do-ftl-transit-time-and-service-compare-to-ltl">
              How do FTL transit time and service compare to LTL?
            </a>
          </li>
          <li>
            <a href="#how-do-ftl-and-ltl-pricing-work-and-their-costs-compare">
              How do FTL and LTL pricing work, and their costs compare?
            </a>
          </li>
          <li>
            <a href="#what-are-the-other-advantages-to-consider-when-choosing-ftl-or-ltl">
              What are the other advantages to consider when choosing FTL or
              LTL?
            </a>
          </li>
          <li>
            <a href="#how-do-you-choose-ftl-or-ltl">
              How do you choose FTL or LTL?
            </a>
          </li>
        </ul>
      </nav>
      <h3 id="what-is-full-truckload-shipping">
        What is Full Truckload shipping?
      </h3>
      <p>
        FTL shipping is a mode of transportation where an entire truck,
        typically utilizing a 48’ or 53’ trailer, is dedicated to a single load
        going to a specific destination. Generally, 24 to 26 standard pallets
        (40” x 48”) can fit on a trailer. This means that the whole truck is
        paid for and filled with goods belonging to one party, which can be the
        shipper, a third party, or the consignee (which is the party receiving
        the goods). Generally speaking, FTL shipping is often used when the
        shipment is too large to transport economically as an LTL shipment or
        when the load requires the exclusive use of the tractor-trailer.
      </p>
      <p>
        Direct FTL shipments are picked up and delivered to the consignee with
        no stops. Often, when a load can be planned economically, FTLs can be
        multi-stop, which means multiple orders can be picked up from the
        shipper and delivered to several consignees during its route. This type
        of multi-stop delivery differs as an alternative method of delivering
        from LTL in that LTL orders generally share space with other companies’
        freight en route for reasons explained below.
      </p>
      <p>
        A typical FTL delivery sequence involves a shipper contacting a
        truckload carrier to inform them that a load is ready. If the chosen
        carrier has capacity available (i.e., a driver and truck available to
        make the delivery) and the rate is acceptable, the driver will arrive at
        the shipper’s dock at the instruction of their dispatcher for live
        loading. In some instances, shippers will keep empty trailers on-site in
        their yards so that loads can be pre-loaded onto trailers to save time
        and space for better resource utilization. The delivery can begin when
        the driver has the paperwork in order and the load secured. Once at the
        consignee (which may or may not require a delivery appointment), the
        trailer is unloaded. From there, the driver is free to make their next
        pickup.
      </p>

      <h3 id="what-is-less-than-truckload-shipping">
        What is Less-Than-Truckload (LTL) Shipping?
      </h3>
      <p>
        LTL shipping is a method of transportation for a freight order that does
        not require an entire trailer’s capacity to haul and is more economical
        to ship combined with other small freight orders moving to and from the
        same general areas managed by different parties. For these shipments
        (often 12 pallets or less), the LTL carrier consolidates multiple
        smaller shipments from different companies and delivers them to their
        respective consignees. This is accomplished by leveraging one or more
        regional terminals the carrier operates.
      </p>
      <p>
        A typical LTL delivery sequence includes a shipper contacting their
        local LTL carrier branch via phone, email, or TMS to provide notice of
        the load’s availability. Paperwork, including the Bill of Lading, is
        filled out and accompanies the shipment during transit. The LTL carrier
        will arrive to pick up the pallet(s) and take them to its local terminal
        for consolidation. The shipment is unloaded at the terminal and then, at
        some point, reloaded onto another trailer with other orders destined for
        the same region. Depending on the destination and carrier’s network,
        off-loading and reloading can happen more than once. Once the goods
        reach the final delivery terminal, the pallets are put on a truck and
        ‘out for delivery.’ Modern LTL carriers provide online tracking for
        customers to see the locations and ETA of shipments in real-time while
        in transit.
      </p>

      <h3 id="how-do-ftl-transit-time-and-service-compare-to-ltl">
        How do FTL transit time and service compare to LTL?
      </h3>
      <p>
        One of the primary benefits of FTL shipping is that it is faster than
        LTL shipping. Because the truck is dedicated to the individual shipment,
        there are no stops to pick up or drop off other freight, which keeps the
        truck moving and requires less time.
      </p>
      <p>
        LTL requires pallets to be often unloaded and reloaded multiple times as
        they move through the carrier’s network of consolidation centers during
        their journey, which can increase the likelihood of damage.
      </p>
      <p>
        Although transit times may be slightly slower, LTL carriers operate
        based on a set schedule which can make pickup and delivery more
        predictable. Such a schedule also provides more consistent capacity
        shippers can use when FTL carriers do not have trucks in the area.
      </p>

      <h3 id="how-do-ftl-and-ltl-pricing-work-and-their-costs-compare">
        How do FTL and LTL pricing work, and their costs compare?
      </h3>
      <p>
        FTL costs are often charged on a per-mile or flat-cost basis that is
        quoted in advance. By comparison, FTL rates are easier to calculate and
        understand compared to LTL. Mileage accuracy is vital to ensure the
        costs are calculated and invoiced correctly, so shippers and carriers
        need to ensure they are using the same mileage calculation method.
      </p>
      <p>
        The obvious pricing advantage of FTL shipping is that it is usually more
        cost-effective than LTL shipping for larger deliveries that take up a
        lot of space. And because the entire truck is dedicated to the shipment,
        the shipper pays a flat rate for the truck. This is regardless of how
        much the shipment weighs or how much product is in it as long as it does
        not exceed the weight limit of the equipment used.
      </p>
      <p>
        A Full Truckload cost is typically a base rate (per-mile or flat rate)
        plus a fuel surcharge and any accessorials incurred. Accessorials can
        include detention, unloading, or other special handling charges. The
        shipper and carrier should have any potential accessorial costs defined
        and agreed to in a rate agreement or contract in advance.
      </p>
      <p>
        A primary benefit of LTL shipping is that it is more cost-effective for
        smaller shipments. Because the cost of the truck is shared among
        multiple shippers, each shipper pays a lower rate than they would for
        FTL shipping. But, LTL pricing is more complex, and the cost also
        depends on the weight and type of goods being shipped more so than for
        FTL shipments.
      </p>
      <p>
        Although they can sometimes be quoted as a flat rate, the main factor
        determining an LTL shipment’s cost is a Freight Classification code. A
        published index usually determines the class called the NMFC (National
        Motor Freight Classification®) and is administered by the National
        American Motor Freight Traffic Association. The NMFC is a highly
        detailed calculation that accounts for weight density and product value.
        Often, companies are offered a discount on their tariff as additional
        savings. LTL shipments are also subject to accessorials and fuel
        surcharges like FTL.
      </p>
      <p>
        The complexity of LTL rates makes technology to calculate shipping rates
        accurately, and auditing invoices a necessity for many companies.
      </p>
      <p>
        An important note is that truckload carriers often offer much more
        competitive rates, enabling them to position drivers and equipment in
        their busiest shipping lanes. Looking for local carriers for your
        shipments is always a good idea as a money-saving technique. The
        downside is these carriers do not always have equipment availability
        when you need it. Similarly, LTL carriers have lanes and regions that
        work best for their networks and where they can offer the best rates.
      </p>

      <h3 id="what-are-the-other-advantages-to-consider-when-choosing-ftl-or-ltl">
        What are the other advantages to consider when choosing FTL or LTL?
      </h3>
      <p>
        Another advantage of LTL shipping is that it can be more flexible than
        FTL shipping. Since the carrier can combine multiple smaller shipments
        into one truck, shippers have more shipping options.
      </p>
      <p>
        The better choice cost-wise between FTL and LTL can be nuanced because
        the lower cost is not always obvious. There are borderline shipments
        when paying for a whole truck, even though the shipment does not require
        the space, is a lower cost option. For example, because a product’s
        class and weight directly influence the cost of the LTL shipment, there
        becomes a point when the cost of LTL, even though it does not require
        all the space, is cheaper to pay for a whole truck. This often becomes a
        factor when the pallet count for an order reaches about 12.
      </p>

      <h3 id="how-do-you-choose-ftl-or-ltl">How do you choose FTL or LTL?</h3>
      <p>
        Shippers need to consider the cost difference between FTL and LTL for
        deliveries with pallet counts between about eight and 12 pallets. But
        really, it depends on the types of goods it most often ships. Higher
        value, denser types of products may be cheaper to ship as a full
        truckload than LTL at lower pallet counts.
      </p>
      <p>
        In summary, FTL shipping is most often ideal for larger shipments that
        require exclusive use of the truck and need to be delivered quickly. In
        comparison, LTL shipping is best for smaller shipments that can be
        consolidated with other shipments to save on shipping costs.
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label:
      'Common Accounting Mistakes Trucking Companies Often Make And How To Avoid Them.',
    url: 'https://blog.loadsmart.com/2022/11/08/common-accounting-mistakes-trucking-companies-often-make-and-how-to-avoid-them',
    external: true,
  },
  {
    label:
      'Where’s My Money? Understanding the Different Ways to Pay Your Drivers.',
    url: 'https://blog.loadsmart.com/2022/11/04/wheres-my-money-understanding-the-different-ways-to-pay-your-carriers',
    external: true,
  },
  {
    label: 'How to Make More Money as a Carrier.',
    url: 'https://blog.loadsmart.com/2022/11/04/how-to-make-more-money-as-a-carrier',
    external: true,
  },
];

const bannerContent = {
  logo: <LogoShipperGuide />,
  title: (
    <>
      Your Multimodal Procurement
      <br /> and Execution Tool
    </>
  ),
  subtitle:
    'Plan, procure and execute freight in one place. Real-time spot and contract price comparison. Access instantly bookable spot rates in multiple modes including FTL, LTL, vLTL and Drayage. Manage your own carrier networks with historical lane performance, tendering, tracking and data insights.',
  cta: 'Get Started',
  url: `https://shipperguide.com/?utm_source=loadsmart-website#request-a-demo`,
  image: {
    small: '/images/home/features/screencapture-app-sm@2x.png',
    medium: '/images/home/features/screencapture-app-xxlg@2x.png',
    large: '/images/home/features/screencapture-app-xxlg@2x.png',
    xxlarge: '/images/home/features/screencapture-app-xxlg@2x.png',
  },
  alt: 'ShipperGuide dashboard screeenshots',
};

export default function WhatIsFTLvsLTL() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="LTL shipping, Less than load, Freight shipping, Partial truckload, Small freight shipping, Consolidated shipping, Less than truckload carriers, Partial load shipping, LTL freight rates, Freight consolidation, Unbranded keywords for Full, Truckload (FTL): Full truckload shipping, Truckload shipping, FTL carriers, Full load trucking, Dedicated truckload, Flatbed truckload, Refrigerated truckload, Heavy haul truckload, Dry van truckload, Bulk truckload"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG}
        noindex={process.env.NOINDEX_FLAG}
        title="What is Full Truckload (FTL) vs. Less-Than-Truckload (LTL) | Freight Brokerage "
        description="Learn about the difference between Full Truckload (FTL) and Less-Than-Truckload (LTL) shipping and the benefits of both freight modes."
        canonical="https://loadsmart.com/content/what-is-ftl-vs-ltl/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-ftl-vs-ltl/',
        }}
      />
      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
      />
    </>
  );
}
